import { useQuery } from '@apollo/client';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { GetSpecialsPageSettingsDocument, MenuFilterChipsSettings } from 'src/gql/graphql';

type SpecialsPageSettings = {
  menuFilterChips: MenuFilterChipsSettings | null;
  showCategoryTiles: boolean | null;
};

// Scoped read of the dispensary-configurable specials-page settings. Kept off the shared
// baseDispensaryFragment (loaded app-wide) so the storefront's global dispensary query is
// unchanged; this query only runs on the specials page.
export function useSpecialsPageSettings(): SpecialsPageSettings {
  const { dispensary } = useDispensary();
  const dispensaryId = dispensary?.id ?? dispensary?.cName;

  const { data } = useQuery(GetSpecialsPageSettingsDocument, {
    variables: { dispensaryFilter: { cNameOrID: dispensaryId ?? '' } },
    skip: !dispensaryId,
  });

  // Match by id rather than trusting index-0: filteredDispensaries is a search-style result, and a
  // prior incident (ECOMM-9255) had a _.head() fallback silently pick the wrong dispensary. No match
  // yields undefined settings, which fails open (all chips + category row shown).
  const matched = data?.filteredDispensaries?.find((d) => d?.id === dispensaryId);
  const settings = matched?.specialsSettings;

  return {
    menuFilterChips: settings?.menuFilterChips ?? null,
    showCategoryTiles: settings?.showCategoryTiles ?? null,
  };
}
